import { useLocale, useServiceUrl, useTemplates, useCountry, useTranslate } from './chunk-UUWHMO43.js';
import { Box } from '@gemini/core';
import { forwardRef, useState } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useForm, FormProvider } from 'react-hook-form';
import { UsageType, FormDistributionType, FormEstateType, RoomsType, FloorsType, ProviderType, PlaceType } from '@aviv-search/types';
import { LocationDotIcon } from '@gemini/icons';
import { Autocomplete } from '@gemini/ui';
import { useDebounce } from 'react-use';
import axios from 'axios';

var RefinerFormDefaultId = "refiner-form-id";
var RefinerFormTestId = "refiner-form-test-id";
var RefinerFormBase = ({ children, onSubmit, formId = RefinerFormDefaultId }, forwardedRef) => /* @__PURE__ */ jsx(
  Box,
  {
    as: "form",
    ref: forwardedRef,
    id: formId,
    onSubmit,
    testId: RefinerFormTestId,
    width: "100%",
    unsafe_style: {
      containerType: "inline-size"
    },
    children
  }
);
var RefinerForm = forwardRef(RefinerFormBase);
var defaultValues = {
  usageType: UsageType.RESIDENTIAL,
  distributionType: FormDistributionType.BUY,
  estateType: FormEstateType.HOUSE_APARTMENT,
  locations: [],
  rooms: { min: RoomsType.ANY, max: RoomsType.ANY },
  bedrooms: { min: RoomsType.ANY, max: RoomsType.ANY },
  features: [],
  estateSubTypes: [],
  floors: [FloorsType.ROOF_STOREY, FloorsType.GROUND_FLOOR, FloorsType.OTHER],
  providerType: [ProviderType.PRIVATE, ProviderType.PROFESSIONAL],
  projectTypes: [],
  energyCertificate: [],
  listingsDisplay: [],
  energyTypes: []
};
var getDefaultValues = (props) => ({
  ...defaultValues,
  ...props
});
var WithFormProviderBase = ({ children, initialModel, formId, onSubmit = () => {
} }, forwardedRef) => {
  const model = getDefaultValues(initialModel);
  const methods = useForm({
    defaultValues: model,
    mode: "onBlur",
    reValidateMode: "onBlur"
  });
  return /* @__PURE__ */ jsx(FormProvider, { ...methods, children: /* @__PURE__ */ jsx(
    RefinerForm,
    {
      ref: forwardedRef,
      formId,
      onSubmit: (e) => {
        methods.handleSubmit(onSubmit)(e).catch(() => {
        });
      },
      children
    }
  ) });
};
var WithFormProvider = forwardRef(WithFormProviderBase);
[
  PlaceType.BOROUGHS,
  PlaceType.NEIGHBORHOOD,
  PlaceType.MUNICIPALITY,
  PlaceType.PROVINCE,
  PlaceType.REGION,
  PlaceType.HOUSE_NUMBER,
  PlaceType.STREETS,
  PlaceType.COUNTRY,
  PlaceType.MACRO_NEIGHBORHOOD,
  PlaceType.MICRO_NEIGHBORHOOD,
  PlaceType.COUNTY,
  PlaceType.POSTCODE
];
var placeTypeLokalizeKey = {
  NBH2: "district",
  NBH3: void 0,
  AD08: "city",
  AD06: "province",
  AD04: "federal_state",
  AD09: "borough",
  AD02: "country",
  AD07: "district",
  NBH1: "macro_neighborhood",
  POCO: "postal_code",
  STRT: "streets",
  HONU: "house_number"
};
var getSuffix = (type, country = "default") => placeTypeLokalizeKey[type] ? useTranslate(`location.autocomplete.suggestion.suffix.${country}.${placeTypeLokalizeKey[type]}`) : "";
var mapParent = ({
  id,
  type_key,
  labels,
  postal_codes,
  coordinates
}) => ({
  id,
  ...type_key ? { type_key } : {},
  ...labels ? { labels } : {},
  postalCodes: postal_codes,
  ...coordinates?.max_inscribed_circle ? { coordinates: coordinates.max_inscribed_circle } : {}
});
var mapPlace = ({ id, type_key, legacyMapping, labels, postal_codes, coordinates, parents = [] }, country) => ({
  id,
  type_key,
  legacyMapping,
  label: labels.join(", "),
  labels,
  postalCodes: postal_codes,
  suffix: getSuffix(type_key, country),
  parents: parents.map(mapParent),
  ...coordinates?.max_inscribed_circle ? { coordinates: coordinates.max_inscribed_circle } : { coordinates }
});
var callAutocomplete = async ({
  input,
  serviceUrl,
  signal,
  locationTypes,
  parentLocationTypes,
  locale,
  country
}) => {
  const { data } = await axios.post(
    `${serviceUrl}/autocomplete`,
    {
      text: input,
      limit: 10,
      placeTypes: locationTypes,
      parentTypes: parentLocationTypes,
      locale
    },
    {
      signal
    }
  );
  return data?.map((place) => mapPlace(place, country));
};
var LocationAutocompleteTestId = "Location-autocomplete-testid";
var minimumInputLength = 2;
var debounceMS = 250;
var LocationAutocomplete = ({
  onSubmit,
  placeholderText,
  ariaLabelText,
  initialInput = "",
  isDisabled = false,
  selectedIds = [],
  inError = false,
  errorMessage = "error",
  onChange = () => {
  }
}) => {
  const [items, setItems] = useState([]);
  const [value, setValue] = useState(initialInput);
  const [shouldDisplayEmptyState, setShouldDisplayEmptyState] = useState(false);
  const locale = useLocale();
  const serviceUrl = useServiceUrl();
  const { locationTypes = [] } = useTemplates().filters;
  const country = useCountry();
  useDebounce(
    async () => {
      if (value?.length < minimumInputLength) {
        setItems([]);
        return;
      }
      const newItems = await callAutocomplete({
        input: value,
        serviceUrl,
        locationTypes,
        locale,
        parentLocationTypes: locationTypes,
        country
      });
      setItems(newItems.filter((item) => !selectedIds.find((id) => item.id === id)));
      setShouldDisplayEmptyState(newItems.length === 0);
    },
    debounceMS,
    [value]
  );
  const handleOnSubmit = (key) => {
    const autocompleteItem = items.find((item) => item.id === key);
    if (autocompleteItem) {
      setItems([]);
      setValue("");
    }
    if (autocompleteItem && key)
      onSubmit(autocompleteItem);
  };
  const autocompleteProps = {
    items,
    value,
    isDisabled,
    shouldFlip: false,
    shouldFocusFirstItem: true,
    placeholder: placeholderText,
    iconStart: LocationDotIcon,
    label: { content: ariaLabelText, isHidden: true },
    menuTrigger: "focus",
    emptyState: value.length >= minimumInputLength && shouldDisplayEmptyState ? {
      title: useTranslate("location.autocomplete.not_found.title"),
      description: useTranslate("location.autocomplete.not_found.subtitle")
    } : void 0,
    onSubmit: (_, key) => handleOnSubmit(key),
    onChange: (input) => {
      onChange();
      setValue(input);
    },
    error: inError ? errorMessage : void 0,
    testId: LocationAutocompleteTestId
  };
  return /* @__PURE__ */ jsx(Autocomplete, { ...autocompleteProps });
};

export { LocationAutocomplete, RefinerFormDefaultId, WithFormProvider, callAutocomplete };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-6IXBTFSD.js.map